import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { calculateStatus } from '@/hooks/data-management/utils';
import { useDataCache } from '@/hooks/useDataCache';

export const useDataFetching = () => {
    const {  user, activeAccountId } = useAuth();
    const { toast } = useToast();
    const { getCachedData, setCachedData, clearCache } = useDataCache();

    // Lookup Data (Operators, Flags, etc)
    const [operators, setOperators] = useState([]);
    const [flags, setFlags] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [lookupsLoaded, setLookupsLoaded] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Helper para normalizar termo de busca (evita quebra de query com vírgulas)
    const normalizeSearchTerm = useCallback((searchTerm) => {
        if (!searchTerm) return { normalized: '', hasComma: false };

        let term = searchTerm.toLowerCase().trim();
        const originalHasComma = term.includes(',');

        // Se parece um número (contém apenas dígitos, vírgula, ponto ou espaços)
        const isNumericSearch = /^[\d\s,\.]+$/.test(term);
        if (isNumericSearch) {
            // Normalizar: remover espaços, trocar vírgula por ponto
            term = term.replace(/\s/g, '').replace(',', '.');
        } else {
            // Para texto, remover vírgulas que podem quebrar a sintaxe OR do Supabase
            term = term.replace(/,/g, '');
        }

        // Escapar caracteres especiais do SQL LIKE
        const normalized = term.replace(/%/g, '\\%').replace(/_/g, '\\_');
        return { normalized, hasComma: originalHasComma && isNumericSearch };
    }, []);

    // 1. Fetch Lookups (Operadoras, etc) - Carrega uma vez só
    const fetchLookups = useCallback(async () => {
        if (!user || lookupsLoaded) return;
        try {
            const [ops, flgs, pms] = await Promise.all([
                supabase.from('operators').select('*, operator_flags(flag_id)').eq('user_id', activeAccountId),
                supabase.from('flags').select('*').eq('user_id', activeAccountId),
                supabase.from('payment_methods').select('*').eq('user_id', activeAccountId)
            ]);

            if (ops.data) setOperators(ops.data.map(op => ({ ...op, flagIds: op.operator_flags?.map(of => of.flag_id) || [] })));
            if (flgs.data) setFlags(flgs.data);
            if (pms.data) setPaymentMethods(pms.data);
            setLookupsLoaded(true);
        } catch (err) {
            console.error("Erro ao carregar tabelas auxiliares", err);
        }
    }, [user, lookupsLoaded, activeAccountId]);

    // 2. Fetch Sales (Server Side Pagination or Client Side if Status Filter active)
    const fetchSales = useCallback(async ({ page, pageSize, filters }) => {
        if (!user) return { data: [], count: 0 };

        try {
            const { normalized: term } = filters?.searchTerm ? normalizeSearchTerm(filters.searchTerm) : { normalized: '' };

            // Verificação crítica: Se houver filtro de STATUS ou solicitação explícita de fetchAll (Relatórios),
            // precisamos buscar TUDO, calcular o status em memória e filtrar no frontend antes de paginar.
            const hasStatusFilter = filters?.selectedStatuses?.size > 0;
            const shouldFetchAll = filters?.fetchAll === true;

            // Busca normal (query base)
            let query = supabase
                .from('sales')
                .select('*', { count: 'exact' })
                .eq('user_id', activeAccountId);

            // Ordenação padrão: por data decrescente
            query = query.order('data', { ascending: false });

            // Função helper para aplicar filtros base (comuns a todas as estratégias)
            const applyBaseFilters = (q) => {
                if (filters) {
                    if (filters.searchTerm && term.length > 0) {
                        const isNumeric = /^[\d\.]+$/.test(term);
                        if (!isNumeric) {
                            q = q.or(`bandeira.ilike.%${term}%,empresa.ilike.%${term}%,codigoautorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                        }
                    }
                    if (filters.dateRange?.startDate) q = q.gte('data', filters.dateRange.startDate.toISOString().split('T')[0]);
                    if (filters.dateRange?.endDate) q = q.lte('data', filters.dateRange.endDate.toISOString().split('T')[0]);
                    if (filters.selectedOperators?.size > 0) q = q.in('empresa', Array.from(filters.selectedOperators));
                    if (filters.selectedFlags?.size > 0) q = q.in('bandeira', Array.from(filters.selectedFlags));
                    if (filters.selectedPaymentMethods?.size > 0) q = q.in('tipotransacao', Array.from(filters.selectedPaymentMethods));
                }
                return q;
            };

            // ESTRATÉGIA 1: Filtragem Complexa (Busca Numérica, status ou Relatório Completo)
            // Se tiver busca numérica OU filtro de status OU fetchAll, precisamos buscar mais dados e processar no cliente
            const isNumericSearch = term && /^[\d\.]+$/.test(term);

            if (hasStatusFilter || isNumericSearch || shouldFetchAll) {
                let allData = [];

                // Lógica de LOOP (Batching) para garantir que pegamos TUDO antes de filtrar em memória.
                // Isso substitui a antiga lógica que fazia 2 queries separadas e limitadas.
                // Agora, Status, Relatório E Busca Numérica usam essa estratégia robusta.

                let from = 0;
                const batchSize = 1000;
                let hasMore = true;

                while (hasMore) {
                    let batchQuery = supabase.from('sales').select('*').eq('user_id', activeAccountId).order('data', { ascending: false }).range(from, from + batchSize - 1);
                    batchQuery = applyBaseFilters(batchQuery);

                    const { data, error } = await batchQuery;
                    if (error) throw error;

                    if (data.length > 0) {
                        allData = [...allData, ...data];
                        from += batchSize;
                        if (data.length < batchSize) hasMore = false;
                    } else {
                        hasMore = false;
                    }

                    // Safety break para não travar em loop infinito se houver milhões
                    if (from > 100000 && !shouldFetchAll) { // Limite maior, ou sem limite se for Relatório
                        console.warn("Muitos registros, interrompendo busca completa.");
                        hasMore = false;
                    }
                }

                // Processar Status em Memória
                const processedData = allData.map(sale => ({
                    ...sale,
                    status: calculateStatus(sale.valorcompra, sale.valorrecebido, sale.parcelas)
                }));

                // Filtrar pelo Status
                let finalFilteredData = processedData;
                if (hasStatusFilter) {
                    finalFilteredData = processedData.filter(s => filters.selectedStatuses.has(s.status));
                }

                // Filtrar por Busca Numérica (se houver)
                if (isNumericSearch) {
                    const numericValue = parseFloat(term);
                    const exactValue = Math.round(numericValue * 100) / 100;

                    finalFilteredData = finalFilteredData.filter(sale => {
                        const vc = Math.round(parseFloat(sale.valorcompra || 0) * 100) / 100;
                        const vr = Math.round(parseFloat(sale.valorrecebido || 0) * 100) / 100;
                        const matchValue = vc === exactValue || vr === exactValue;

                        // Também verificar campos de texto caso o número seja parte de um ID
                        const matchText = (sale.bandeira && sale.bandeira.toLowerCase().includes(term.toLowerCase())) ||
                            (sale.codigoautorizacao && sale.codigoautorizacao.toLowerCase().includes(term.toLowerCase())) ||
                            (sale.codigopedido && sale.codigopedido.toLowerCase().includes(term.toLowerCase()));

                        return matchValue || matchText;
                    });
                }

                // Ordenar (já deve vir ordenado do banco, mas ao misturar queries ou filtrar pode perder)
                if (isNumericSearch) {
                    finalFilteredData.sort((a, b) => new Date(b.data) - new Date(a.data));
                }

                // Paginação Manual (Client-side)
                const totalCount = finalFilteredData.length;
                const pageFrom = (page - 1) * pageSize;
                const pageTo = pageFrom + pageSize;
                const paginatedData = finalFilteredData.slice(pageFrom, pageTo);

                return { data: paginatedData, count: totalCount };

            } else {
                // ESTRATÉGIA 2: Busca Padrão (Sem filtro de status complexo)
                // Usamos paginação no banco, que é mais rápido

                query = applyBaseFilters(query);

                // Paginação
                const from = (page - 1) * pageSize;
                const to = from + pageSize - 1;
                query = query.range(from, to);

                const { data, count, error } = await query;
                if (error) throw error;

                // Processar status apenas para exibição
                const processedData = data.map(sale => ({
                    ...sale,
                    status: calculateStatus(sale.valorcompra, sale.valorrecebido, sale.parcelas)
                }));

                return { data: processedData, count: count || 0 };
            }

        } catch (error) {
            console.error('Erro ao buscar vendas:', error);
            toast({ title: "Erro na busca", description: error.message, variant: "destructive" });
            return { data: [], count: 0 };
        }
    }, [user, toast, normalizeSearchTerm, activeAccountId]);

    // 3. Fetch Sales Summary (Cards)
    const fetchSalesSummary = useCallback(async ({ filters }) => {
        if (!user) return { totalVendas: 0, totalRecebido: 0, vendasQuitadas: 0, count: 0 };

        try {
            // Normalizar termo de busca (vírgula convertida para ponto)
            const { normalized: searchTermNormalized } = filters?.searchTerm ? normalizeSearchTerm(filters.searchTerm) : { normalized: null };

            // Tentar usar RPC primeiro (Rápido)
            const rpcParams = {
                p_user_id: activeAccountId,
                p_search_term: searchTermNormalized || null,
                p_start_date: filters?.dateRange?.startDate ? filters.dateRange.startDate.toISOString().split('T')[0] : null,
                p_end_date: filters?.dateRange?.endDate ? filters.dateRange.endDate.toISOString().split('T')[0] : null,
                p_operators: filters?.selectedOperators?.size > 0 ? Array.from(filters.selectedOperators) : null,
                p_flags: filters?.selectedFlags?.size > 0 ? Array.from(filters.selectedFlags) : null,
                p_payment_methods: filters?.selectedPaymentMethods?.size > 0 ? Array.from(filters.selectedPaymentMethods) : null,
                p_statuses: filters?.selectedStatuses?.size > 0 ? Array.from(filters.selectedStatuses) : null
            };

            const { data: rpcData, error: rpcError } = await supabase.rpc('get_sales_summary', rpcParams);

            // Se RPC funcionou e retornou dados válidos, usar
            if (!rpcError && rpcData && rpcData.length > 0) {
                const result = {
                    totalVendas: parseFloat(rpcData[0].total_vendas || 0),
                    totalRecebido: parseFloat(rpcData[0].total_recebido || 0),
                    vendasQuitadas: parseInt(rpcData[0].vendas_quitadas || 0),
                    count: parseInt(rpcData[0].total_count || 0)
                };

                console.log("RPC get_sales_summary retornou:", result, "Filtros aplicados:", {
                    searchTerm: filters?.searchTerm,
                    operators: filters?.selectedOperators?.size,
                    flags: filters?.selectedFlags?.size,
                    statuses: filters?.selectedStatuses?.size
                });

                // Se RPC retornou 0 mas há termo de busca, pode ser que não encontrou
                // Nesse caso, usar fallback
                if (result.count === 0 && filters?.searchTerm) {
                    console.warn("RPC retornou 0 resultados com termo de busca. Usando fallback.");
                } else {
                    // Validar apenas se os valores são números válidos
                    const hasSuspiciousZero = result.count > 0 && result.vendasQuitadas === 0 && result.totalRecebido > 0;

                    // Validar consistência: se % recebido é muito maior que % quitadas, há problema
                    const recebidoRatio = result.totalVendas > 0 ? (result.totalRecebido / result.totalVendas) : 0;
                    const quitadasRatio = result.count > 0 ? (result.vendasQuitadas / result.count) : 0;
                    const ratioDifference = recebidoRatio - quitadasRatio;

                    const hasLowQuitadasRatio = result.count > 50 &&
                        result.vendasQuitadas > 0 &&
                        recebidoRatio > 0.6 &&
                        quitadasRatio < 0.5 &&
                        ratioDifference > 0.25;

                    if (!isNaN(result.vendasQuitadas) && !isNaN(result.totalVendas) && !isNaN(result.totalRecebido)) {
                        if (hasSuspiciousZero || hasLowQuitadasRatio) {
                            if (hasLowQuitadasRatio) {
                                console.warn("⚠️ RPC retornou proporção suspeita de vendas quitadas. Usando fallback para recalcular.", {
                                    quitadas: result.vendasQuitadas,
                                    total: result.count,
                                    ratio: (result.vendasQuitadas / result.count).toFixed(2),
                                    recebidoRatio: (result.totalRecebido / result.totalVendas).toFixed(2)
                                });
                            } else {
                                console.warn("⚠️ RPC retornou vendas quitadas = 0 mas há recebimentos. Usando fallback para recalcular.", result);
                            }
                        } else {
                            console.log("✅ RPC retornou valores válidos, usando resultado da RPC");
                            return result;
                        }
                    } else {
                        console.warn("RPC retornou valores inválidos (NaN), usando fallback", result);
                    }
                }
            } else if (rpcError) {
                console.warn("RPC get_sales_summary retornou erro:", rpcError);
            }

            // Fallback (Lento): Buscar TODOS os dados com paginação completa
            console.warn("RPC get_sales_summary falhou ou não existe. Usando fallback (pode ser mais lento).");

            const term = searchTermNormalized || '';
            const isNumericSearch = term && /^[\d\.]+$/.test(term);
            const numericValue = isNumericSearch ? parseFloat(term) : null;
            // Arredondar para 2 casas decimais para comparação exata
            const exactValue = numericValue !== null ? Math.round(numericValue * 100) / 100 : null;

            // Se é busca numérica, fazer duas buscas separadas e combinar
            if (isNumericSearch && exactValue !== null) {
                // Busca 1: campos de texto
                let textQuery = supabase.from('sales').select('valorcompra, valorrecebido, parcelas');
                // Busca 2: campos numéricos - buscar todos e filtrar exatamente depois
                let numericQuery = supabase.from('sales').select('valorcompra, valorrecebido, parcelas');

                // Aplicar filtros de texto
                if (filters) {
                    if (term.length > 0) {
                        textQuery = textQuery.or(`bandeira.ilike.%${term}%,empresa.ilike.%${term}%,codigoautorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                    }
                    if (filters.dateRange?.startDate) {
                        textQuery = textQuery.gte('data', filters.dateRange.startDate.toISOString().split('T')[0]);
                        numericQuery = numericQuery.gte('data', filters.dateRange.startDate.toISOString().split('T')[0]);
                    }
                    if (filters.dateRange?.endDate) {
                        textQuery = textQuery.lte('data', filters.dateRange.endDate.toISOString().split('T')[0]);
                        numericQuery = numericQuery.lte('data', filters.dateRange.endDate.toISOString().split('T')[0]);
                    }
                    if (filters.selectedOperators?.size > 0) {
                        textQuery = textQuery.in('empresa', Array.from(filters.selectedOperators));
                        numericQuery = numericQuery.in('empresa', Array.from(filters.selectedOperators));
                    }
                    if (filters.selectedFlags?.size > 0) {
                        textQuery = textQuery.in('bandeira', Array.from(filters.selectedFlags));
                        numericQuery = numericQuery.in('bandeira', Array.from(filters.selectedFlags));
                    }
                    if (filters.selectedPaymentMethods?.size > 0) {
                        textQuery = textQuery.in('tipotransacao', Array.from(filters.selectedPaymentMethods));
                        numericQuery = numericQuery.in('tipotransacao', Array.from(filters.selectedPaymentMethods));
                    }
                }

                // Buscar todos os dados (sem paginação para poder filtrar numericamente)
                const [textResult, numericResult] = await Promise.all([
                    textQuery.order('data', { ascending: false }),
                    numericQuery.order('data', { ascending: false })
                ]);

                if (textResult.error) throw textResult.error;
                if (numericResult.error) throw numericResult.error;

                // Filtrar numericamente os resultados da busca numérica - comparação EXATA
                const numericFiltered = (numericResult.data || []).filter(s => {
                    const vc = Math.round(parseFloat(s.valorcompra || 0) * 100) / 100;
                    const vr = Math.round(parseFloat(s.valorrecebido || 0) * 100) / 100;
                    return vc === exactValue || vr === exactValue;
                });

                // Combinar resultados, usando Map para remover duplicatas por valores
                const combined = [...(textResult.data || []), ...numericFiltered];
                const uniqueData = Array.from(new Map(combined.map((item, idx) => [JSON.stringify(item), item])).values());

                // Aplicar filtro de status se necessário
                let filteredData = uniqueData;
                if (filters?.selectedStatuses?.size > 0) {
                    filteredData = uniqueData.filter(s => {
                        const status = calculateStatus(parseFloat(s.valorcompra || 0), parseFloat(s.valorrecebido || 0), s.parcelas);
                        return filters.selectedStatuses.has(status);
                    });
                }

                let tv = 0, tr = 0, vq = 0;
                filteredData.forEach(s => {
                    const vc = parseFloat(s.valorcompra || 0);
                    const vr = parseFloat(s.valorrecebido || 0);
                    tv += vc;
                    tr += vr;
                    if (calculateStatus(vc, vr, s.parcelas) === 'Quitado') vq++;
                });

                console.log(`Fallback (numérico): Total de registros processados: ${filteredData.length}, Vendas Quitadas calculadas: ${vq}`);
                return { totalVendas: tv, totalRecebido: tr, vendasQuitadas: vq, count: filteredData.length };
            }

            // Busca normal (texto ou sem busca numérica)
            let allData = [];
            let from = 0;
            const batchSize = 1000;
            let hasMore = true;
            let totalCount = 0;

            // Primeiro, obter o count total
            let countQuery = supabase.from('sales').select('*', { count: 'exact', head: true }).eq('user_id', activeAccountId);
            if (filters) {
                if (filters.searchTerm && term.length > 0) {
                    countQuery = countQuery.or(`bandeira.ilike.%${term}%,empresa.ilike.%${term}%,codigoautorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                }
                if (filters.dateRange?.startDate) countQuery = countQuery.gte('data', filters.dateRange.startDate.toISOString().split('T')[0]);
                if (filters.dateRange?.endDate) countQuery = countQuery.lte('data', filters.dateRange.endDate.toISOString().split('T')[0]);
                if (filters.selectedOperators?.size > 0) countQuery = countQuery.in('empresa', Array.from(filters.selectedOperators));
                if (filters.selectedFlags?.size > 0) countQuery = countQuery.in('bandeira', Array.from(filters.selectedFlags));
                if (filters.selectedPaymentMethods?.size > 0) countQuery = countQuery.in('tipotransacao', Array.from(filters.selectedPaymentMethods));
            }
            const { count } = await countQuery;
            totalCount = count || 0;

            // Buscar todos os dados em lotes
            while (hasMore) {
                let query = supabase.from('sales').select('valorcompra, valorrecebido, parcelas').eq('user_id', activeAccountId);

                if (filters) {
                    if (filters.searchTerm && term.length > 0) {
                        query = query.or(`bandeira.ilike.%${term}%,empresa.ilike.%${term}%,codigoautorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                    }
                    if (filters.dateRange?.startDate) query = query.gte('data', filters.dateRange.startDate.toISOString().split('T')[0]);
                    if (filters.dateRange?.endDate) query = query.lte('data', filters.dateRange.endDate.toISOString().split('T')[0]);
                    if (filters.selectedOperators?.size > 0) query = query.in('empresa', Array.from(filters.selectedOperators));
                    if (filters.selectedFlags?.size > 0) query = query.in('bandeira', Array.from(filters.selectedFlags));
                    if (filters.selectedPaymentMethods?.size > 0) query = query.in('tipotransacao', Array.from(filters.selectedPaymentMethods));
                }

                query = query.order('data', { ascending: false }).range(from, from + batchSize - 1);
                const { data, error } = await query;

                if (error) throw error;

                if (data && data.length > 0) {
                    allData = [...allData, ...data];
                    from += batchSize;
                    hasMore = data.length === batchSize && from < totalCount;
                } else {
                    hasMore = false;
                }
            }

            if (!allData || allData.length === 0) {
                return { totalVendas: 0, totalRecebido: 0, vendasQuitadas: 0, count: totalCount };
            }

            // Aplicar filtro de status se necessário
            let filteredData = allData;
            if (filters?.selectedStatuses?.size > 0) {
                filteredData = allData.filter(s => {
                    const status = calculateStatus(parseFloat(s.valorcompra || 0), parseFloat(s.valorrecebido || 0), s.parcelas);
                    return filters.selectedStatuses.has(status);
                });
            }

            let tv = 0, tr = 0, vq = 0;
            filteredData.forEach(s => {
                const vc = parseFloat(s.valorcompra || 0);
                const vr = parseFloat(s.valorrecebido || 0);
                tv += vc;
                tr += vr;
                const status = calculateStatus(vc, vr, s.parcelas);
                if (status === 'Quitado') vq++;
            });

            console.log(`Fallback: Total de registros processados: ${filteredData.length}, Vendas Quitadas calculadas: ${vq}`);

            return { totalVendas: tv, totalRecebido: tr, vendasQuitadas: vq, count: filteredData.length };

        } catch (error) {
            console.error('Erro no sumário de vendas:', error);
            return { totalVendas: 0, totalRecebido: 0, vendasQuitadas: 0, count: 0 };
        }
    }, [user, normalizeSearchTerm, activeAccountId]);

    // 4. Fetch Receipts
    const fetchReceipts = useCallback(async ({ page, pageSize, filters }) => {
        if (!user) return { data: [], count: 0 };
        try {
            const { normalized: term } = filters?.searchTerm ? normalizeSearchTerm(filters.searchTerm) : { normalized: '' };

            // Converter nomes de operadoras em IDs
            let operatorIds = null;
            if (filters?.selectedOperators?.size > 0 && operators.length > 0) {
                operatorIds = operators
                    .filter(op => filters.selectedOperators.has(op.name))
                    .map(op => op.id);
                if (operatorIds.length === 0) {
                    // Se nenhuma operadora foi encontrada, retornar vazio
                    return { data: [], count: 0 };
                }
            }

            // Verificar se o termo é numérico
            const isNumeric = term && /^[\d\.]+$/.test(term);

            if (isNumeric) {
                // ESTRATÉGIA: Filtragem em Memória para Busca Numérica (Batch Loop)
                // Garante que encontremos o valor exato em todo o histórico, não apenas nas últimas N vendas.

                let allData = [];
                let from = 0;
                const batchSize = 1000;
                let hasMore = true;

                // Loop para buscar TUDO
                while (hasMore) {
                    let batchQuery = supabase
                        .from('receipts')
                        .select('*, operators(name)')
                        .eq('user_id', activeAccountId)
                        .order('data_pagamento', { ascending: false })
                        .range(from, from + batchSize - 1);

                    // Aplicar filtros base (Data, Operadora, Bandeira, Tipo)
                    if (filters.dateRange?.startDate) batchQuery = batchQuery.gte('data_pagamento', filters.dateRange.startDate.toISOString().split('T')[0]);
                    if (filters.dateRange?.endDate) batchQuery = batchQuery.lte('data_pagamento', filters.dateRange.endDate.toISOString().split('T')[0]);
                    if (operatorIds && operatorIds.length > 0) batchQuery = batchQuery.in('operator_id', operatorIds);
                    if (filters.selectedFlags?.size > 0) batchQuery = batchQuery.in('bandeira', Array.from(filters.selectedFlags));
                    if (filters.selectedPaymentMethods?.size > 0) batchQuery = batchQuery.in('tipo', Array.from(filters.selectedPaymentMethods));

                    const { data, error } = await batchQuery;
                    if (error) throw error;

                    if (data && data.length > 0) {
                        allData = [...allData, ...data];
                        from += batchSize;
                        if (data.length < batchSize) hasMore = false;
                    } else {
                        hasMore = false;
                    }

                    // Safety break
                    if (from > 100000) {
                        console.warn("Muitos registros (>100k) em recebimentos, interrompendo busca.");
                        hasMore = false;
                    }
                }

                // Filtrar em memória
                const numericValue = parseFloat(term);
                const exactValue = Math.round(numericValue * 100) / 100;

                const filteredData = allData.filter(receipt => {
                    const valor = Math.round(parseFloat(receipt.valor || 0) * 100) / 100;
                    const matchValue = valor === exactValue;

                    // Também verificar campos de texto caso o número seja parte de um ID
                    const matchText = (receipt.bandeira && receipt.bandeira.toLowerCase().includes(term.toLowerCase())) ||
                        (receipt.codigo_autorizacao && receipt.codigo_autorizacao.toLowerCase().includes(term.toLowerCase())) ||
                        (receipt.codigopedido && receipt.codigopedido.toLowerCase().includes(term.toLowerCase()));

                    return matchValue || matchText;
                });

                // Ordenar (Recente primeiro)
                filteredData.sort((a, b) => new Date(b.data_pagamento) - new Date(a.data_pagamento));

                // Paginação Manual
                const totalCount = filteredData.length;
                const pageFrom = (page - 1) * pageSize;
                const pageTo = pageFrom + pageSize;
                const paginatedData = filteredData.slice(pageFrom, pageTo);

                return { data: paginatedData, count: totalCount };
            }

            // Busca normal (texto ou sem busca numérica)
            let query = supabase
                .from('receipts')
                .select('*, operators(name)', { count: 'exact' })
                .eq('user_id', activeAccountId);

            // Ordenação padrão: por data_pagamento decrescente (mais recente primeiro)
            query = query.order('data_pagamento', { ascending: false });

            if (filters) {
                if (filters.searchTerm && term.length > 0) {
                    query = query.or(`bandeira.ilike.%${term}%,codigo_autorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                }
                if (filters.dateRange?.startDate) query = query.gte('data_pagamento', filters.dateRange.startDate.toISOString().split('T')[0]);
                if (filters.dateRange?.endDate) query = query.lte('data_pagamento', filters.dateRange.endDate.toISOString().split('T')[0]);
                if (operatorIds && operatorIds.length > 0) query = query.in('operator_id', operatorIds);
                if (filters.selectedFlags?.size > 0) query = query.in('bandeira', Array.from(filters.selectedFlags));
                if (filters.selectedPaymentMethods?.size > 0) query = query.in('tipo', Array.from(filters.selectedPaymentMethods));
            }

            const from = (page - 1) * pageSize;
            const to = from + pageSize - 1;
            query = query.range(from, to);

            const { data, count, error } = await query;
            if (error) throw error;

            return { data: data || [], count: count || 0 };
        } catch (err) {
            console.error("Erro receipts", err);
            return { data: [], count: 0 };
        }
    }, [user, normalizeSearchTerm, operators, activeAccountId]);

    // 5. Fetch Receipts Summary
    const fetchReceiptsSummary = useCallback(async ({ filters }) => {
        if (!user) return { totalValor: 0, count: 0 };
        try {
            // Tentar RPC (mas se falhar, usar fallback imediatamente)
            const searchTermNormalized = filters?.searchTerm ? normalizeSearchTerm(filters.searchTerm).normalized : null;
            const rpcParams = {
                p_user_id: activeAccountId,
                p_search_term: searchTermNormalized || null,
                p_start_date: filters?.dateRange?.startDate ? filters.dateRange.startDate.toISOString().split('T')[0] : null,
                p_end_date: filters?.dateRange?.endDate ? filters.dateRange.endDate.toISOString().split('T')[0] : null,
                p_operators: filters?.selectedOperators?.size > 0 ? Array.from(filters.selectedOperators) : null,
                p_flags: filters?.selectedFlags?.size > 0 ? Array.from(filters.selectedFlags) : null,
                p_payment_methods: filters?.selectedPaymentMethods?.size > 0 ? Array.from(filters.selectedPaymentMethods) : null
            };

            let rpcData = null;
            let rpcError = null;
            let useFallback = false;

            // Forçar fallback se houver filtro de formas de pagamento (RPC pode não suportar ou ignorar)
            if (filters?.selectedPaymentMethods?.size > 0) {
                useFallback = true;
            }

            try {
                const result = await supabase.rpc('get_receipts_summary', rpcParams);
                rpcData = result.data;
                rpcError = result.error;

                // Se houver erro ou se o erro indica problema de coluna, usar fallback
                if (rpcError) {
                    // Erro 42703 = column does not exist (problema de schema)
                    if (rpcError.code === '42703' || rpcError.message?.includes('does not exist')) {
                        useFallback = true;
                        console.warn("RPC get_receipts_summary tem problema de schema. Usando fallback.", rpcError);
                    } else {
                        // Outros erros também podem indicar problema
                        useFallback = true;
                        console.warn("RPC get_receipts_summary falhou. Usando fallback.", rpcError);
                    }
                }
            } catch (err) {
                // Capturar erros de execução da RPC
                rpcError = err;
                useFallback = true;
                console.warn("Erro ao executar RPC get_receipts_summary. Usando fallback:", err);
            }

            // Se a RPC funcionou e retornou dados válidos, usar o resultado
            if (!useFallback && !rpcError && rpcData && rpcData.length > 0) {
                return {
                    totalValor: parseFloat(rpcData[0].total_valor || 0),
                    count: parseInt(rpcData[0].total_count || 0)
                };
            }

            // Fallback: Buscar TODOS os dados com paginação completa
            if (useFallback || rpcError) {
                console.warn("Usando fallback para get_receipts_summary (pode ser mais lento).");
            }

            // Converter nomes de operadoras em IDs
            let operatorIds = null;
            if (filters?.selectedOperators?.size > 0 && operators.length > 0) {
                operatorIds = operators
                    .filter(op => filters.selectedOperators.has(op.name))
                    .map(op => op.id);
                if (operatorIds.length === 0) {
                    // Se nenhuma operadora foi encontrada, retornar vazio
                    return { totalValor: 0, count: 0 };
                }
            }

            const term = searchTermNormalized || '';
            const isNumericSearch = term && /^[\d\.]+$/.test(term);
            const numericValue = isNumericSearch ? parseFloat(term) : null;
            // Arredondar para 2 casas decimais para comparação exata
            const exactValue = numericValue !== null ? Math.round(numericValue * 100) / 100 : null;

            // Se é busca numérica, fazer duas buscas separadas e combinar
            if (isNumericSearch && exactValue !== null) {
                // Busca 1: campos de texto
                let textQuery = supabase.from('receipts').select('id, valor, bandeira, data_pagamento');
                // Busca 2: campos numéricos - buscar todos e filtrar exatamente depois
                let numericQuery = supabase.from('receipts').select('id, valor, bandeira, data_pagamento');

                // Aplicar filtros de texto
                if (filters) {
                    if (term.length > 0) {
                        textQuery = textQuery.or(`bandeira.ilike.%${term}%,codigo_autorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                    }
                    if (filters.dateRange?.startDate) {
                        textQuery = textQuery.gte('data_pagamento', filters.dateRange.startDate.toISOString().split('T')[0]);
                        numericQuery = numericQuery.gte('data_pagamento', filters.dateRange.startDate.toISOString().split('T')[0]);
                    }
                    if (filters.dateRange?.endDate) {
                        textQuery = textQuery.lte('data_pagamento', filters.dateRange.endDate.toISOString().split('T')[0]);
                        numericQuery = numericQuery.lte('data_pagamento', filters.dateRange.endDate.toISOString().split('T')[0]);
                    }
                    if (operatorIds && operatorIds.length > 0) {
                        textQuery = textQuery.in('operator_id', operatorIds);
                        numericQuery = numericQuery.in('operator_id', operatorIds);
                    }
                    if (filters.selectedFlags?.size > 0) {
                        textQuery = textQuery.in('bandeira', Array.from(filters.selectedFlags));
                        numericQuery = numericQuery.in('bandeira', Array.from(filters.selectedFlags));
                    }
                    if (filters.selectedPaymentMethods?.size > 0) {
                        textQuery = textQuery.in('tipo', Array.from(filters.selectedPaymentMethods));
                        numericQuery = numericQuery.in('tipo', Array.from(filters.selectedPaymentMethods));
                    }
                }

                // Buscar todos os dados (sem paginação para poder filtrar numericamente)
                const [textResult, numericResult] = await Promise.all([
                    textQuery.order('data_pagamento', { ascending: false }),
                    numericQuery.order('data_pagamento', { ascending: false })
                ]);

                if (textResult.error) throw textResult.error;
                if (numericResult.error) throw numericResult.error;

                // Filtrar numericamente os resultados da busca numérica - comparação EXATA
                const numericFiltered = (numericResult.data || []).filter(r => {
                    const valor = Math.round(parseFloat(r.valor || 0) * 100) / 100;
                    return valor === exactValue;
                });

                // Combinar resultados, usando Map para remover duplicatas por ID
                const combined = [...(textResult.data || []), ...numericFiltered];
                const uniqueIds = Array.from(new Set(combined.map(r => r.id).filter(Boolean)));

                // Buscar dados completos para calcular totais
                let uniqueData = [];
                if (uniqueIds.length > 0) {
                    let fullDataQuery = supabase.from('receipts').select('id, valor').eq('user_id', activeAccountId).in('id', uniqueIds);
                    const fullData = await fullDataQuery;
                    if (fullData.data) {
                        uniqueData = fullData.data;
                    }
                }

                const total = uniqueData.reduce((sum, r) => sum + (parseFloat(r.valor) || 0), 0);
                return { totalValor: total, count: uniqueData.length };
            }

            // Busca normal (texto ou sem busca numérica)
            let allData = [];
            let from = 0;
            const batchSize = 1000;
            let hasMore = true;
            let totalCount = 0;

            // Primeiro, obter o count total
            let countQuery = supabase.from('receipts').select('*', { count: 'exact', head: true }).eq('user_id', activeAccountId);
            if (filters) {
                if (filters.searchTerm && term.length > 0) {
                    countQuery = countQuery.or(`bandeira.ilike.%${term}%,codigo_autorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                }
                if (filters.dateRange?.startDate) countQuery = countQuery.gte('data_pagamento', filters.dateRange.startDate.toISOString().split('T')[0]);
                if (filters.dateRange?.endDate) countQuery = countQuery.lte('data_pagamento', filters.dateRange.endDate.toISOString().split('T')[0]);
                if (operatorIds && operatorIds.length > 0) countQuery = countQuery.in('operator_id', operatorIds);
                if (filters.selectedFlags?.size > 0) countQuery = countQuery.in('bandeira', Array.from(filters.selectedFlags));
                if (filters.selectedPaymentMethods?.size > 0) countQuery = countQuery.in('tipo', Array.from(filters.selectedPaymentMethods));
            }
            const { count } = await countQuery;
            totalCount = count || 0;

            // Buscar todos os dados em lotes
            while (hasMore) {
                let query = supabase.from('receipts').select('valor').eq('user_id', activeAccountId);

                if (filters) {
                    if (filters.searchTerm && term.length > 0) {
                        query = query.or(`bandeira.ilike.%${term}%,codigo_autorizacao.ilike.%${term}%,codigopedido.ilike.%${term}%`);
                    }
                    if (filters.dateRange?.startDate) query = query.gte('data_pagamento', filters.dateRange.startDate.toISOString().split('T')[0]);
                    if (filters.dateRange?.endDate) query = query.lte('data_pagamento', filters.dateRange.endDate.toISOString().split('T')[0]);
                    if (operatorIds && operatorIds.length > 0) query = query.in('operator_id', operatorIds);
                    if (filters.selectedFlags?.size > 0) query = query.in('bandeira', Array.from(filters.selectedFlags));
                    if (filters.selectedPaymentMethods?.size > 0) query = query.in('tipo', Array.from(filters.selectedPaymentMethods));
                }

                query = query.order('data_pagamento', { ascending: false }).range(from, from + batchSize - 1);
                const { data, error } = await query;

                if (error) throw error;

                if (data && data.length > 0) {
                    allData = [...allData, ...data];
                    from += batchSize;
                    hasMore = data.length === batchSize && from < totalCount;
                } else {
                    hasMore = false;
                }
            }

            const total = allData ? allData.reduce((sum, r) => sum + (parseFloat(r.valor) || 0), 0) : 0;
            return { totalValor: total, count: totalCount };

        } catch (err) {
            return { totalValor: 0, count: 0 };
        }
    }, [user, normalizeSearchTerm, operators, activeAccountId]);

    // Inicialização
    useEffect(() => {
        setLookupsLoaded(false);
    }, [activeAccountId]);

    useEffect(() => {
        fetchLookups();
    }, [fetchLookups]);

    const forceReloadData = useCallback(() => {
        setLookupsLoaded(false);
        setRefreshTrigger(prev => prev + 1);
    }, []);

    const checkOrphanReceipts = useCallback(async () => {
        if (!user) throw new Error('Usuário não autenticado');

        try {
            const { data, error } = await supabase.rpc('check_orphan_receipts', {
                p_user_id: activeAccountId
            });

            if (error) throw error;

            const orphanReceipts = data || [];
            // O RPC já deve retornar os dados formatados, mas caso precise de algum cálculo extra:
            const totalOrphanValue = orphanReceipts.reduce((acc, curr) => acc + (parseFloat(curr.valor) || 0), 0);

            return {
                orphanReceipts,
                summary: {
                    orphanReceipts: orphanReceipts.length,
                    totalOrphanValue,
                    totalReceipts: 0 // O RPC original talvez não retorne o total geral, mas o foco é nos órfãos
                }
            };
        } catch (error) {
            console.error('Erro em checkOrphanReceipts:', error);
            throw error;
        }
    }, [user]);

    return {
        operators,
        setOperators,
        flags,
        setFlags,
        paymentMethods,
        setPaymentMethods,
        fetchSales,
        fetchSalesSummary,
        fetchReceipts,
        fetchReceiptsSummary,
        forceReloadData,
        refreshTrigger,
        checkOrphanReceipts
    };
};

